import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faCouch, 
  faPalette, 
  faGem, 
  faClock,
  faStore,
  faShoppingBag,
  faCalendarAlt,
  faTimes,
  faInfoCircle,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';

// Import placeholder image - replace with actual shop image
// Import custom hook to force retail section
import { useRetailSection } from '../../../hooks/useRetailSection';

const retailImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/retail3.webp';

const retailBanner = 'https://BelvoirCastle.b-cdn.net/Banners/retailBan2.webp';

const DuchessContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const ComingSoonBanner = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 3rem 2rem;
  text-align: center;
  margin: 2rem 0 4rem;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: white;
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 0);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: white;
    clip-path: polygon(0 0, 100% 100%, 0 100%);
  }
`;

const ComingSoonContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const ComingSoonTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.8rem;
  color: white;
  margin-bottom: 1rem;
`;

const ComingSoonDate = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.accent};
`;

const ComingSoonText = styled.p`
  font-size: 1.3rem;
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const CurrentTimeDisplay = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 3rem;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 500;
`;

const OpeningTimesCard = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto 4rem;
`;

const CardImage = styled.div`
  height: 250px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const CardContent = styled.div`
  padding: 2rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 1rem 0;
`;

const StatusIndicator = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  margin-right: 8px;
`;

const StatusText = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  display: flex;
  align-items: center;
`;

const TimeInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const DayLabel = styled.div`
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
`;

const Hours = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.6rem;
  font-weight: 500;
`;

const Note = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 0.5rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  
  svg {
    color: white;
    font-size: 32px;
  }
`;

const FeatureTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
`;

const UpcomingDatesSection = styled.div`
  margin: 4rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpcomingDatesTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DateCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.$type === 'closed') return 'rgba(244, 67, 54, 0.8)';
    if (props.$type === 'special') return 'rgba(255, 165, 0, 0.8)';
    return 'rgba(34, 139, 34, 0.8)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DateTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRange = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.8rem;
`;

const DateDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const DateNote = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
`;

const EmailSignupSection = styled.div`
  background-color: ${props => props.theme.colors.greyBackground};
  padding: 3rem 2rem;
  border-radius: 8px;
  text-align: center;
  margin: 4rem 0;
`;

const EmailSignupTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const EmailSignupText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto 2rem;
`;

const EmailForm = styled.form`
  display: flex;
  max-width: 500px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const EmailInput = styled.input`
  flex-grow: 1;
  padding: 0.8rem 1rem;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 4px 0 0 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
  
  @media (max-width: 768px) {
    border-radius: 4px;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 2px solid transparent;
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    border-radius: 50px;
  }
`;

const ShopButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin: 2rem auto;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => props.isOpen ? 'rgba(76, 175, 80, 0.85)' : 'rgba(244, 67, 54, 0.85)'};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const DuchessCollection = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [email, setEmail] = useState('');
  
  // Use custom hook to ensure we stay in retail section
  useRetailSection();
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);
  
  // Format current time
  const formattedTime = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
  
  // Get dynamic opening hours based on current date
  const getOpeningHours = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Christmas Day - Closed
    if (year === 2025 && month === 11 && day === 25) {
      return {
        days: "Closed",
        times: "Closed",
        note: "Christmas Day - Closed"
      };
    }
    
    // Boxing Day (Dec 26, 2025) - 9am-3:30pm
    if (year === 2025 && month === 11 && day === 26) {
      return {
        days: "Boxing Day",
        times: "09:00 - 15:30",
        note: "Special Boxing Day hours"
      };
    }
    
    // New Year's Day (Jan 1, 2026) - 9am-3:30pm
    if (year === 2026 && month === 0 && day === 1) {
      return {
        days: "New Year's Day",
        times: "09:00 - 15:30",
        note: "Special New Year's Day hours"
      };
    }
    
    // Default: Open daily 9am-5pm
    return {
      days: "Daily",
      times: "09:00 - 17:00",
      note: ""
    };
  };
  
  const openingHours = getOpeningHours();
  
  // Function to check if the shop is currently open
  const isShopOpen = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Christmas Day - Closed
    if (year === 2025 && month === 11 && day === 25) {
      return false;
    }
    
    // Parse opening hours
    const timeRange = openingHours.times.split('-').map(t => t.trim());
    if (timeRange.length !== 2 || timeRange[0] === 'Closed') return false;
    
    // Convert times to 24-hour format for comparison
    const [openHour, openMinute] = timeRange[0].split(':').map(Number);
    const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);
    
    const openTime = new Date();
    openTime.setHours(openHour, openMinute || 0, 0);
    
    const closeTime = new Date();
    closeTime.setHours(closeHour, closeMinute || 0, 0);
    
    // Check if current time is between open and close times
    return currentTime >= openTime && currentTime <= closeTime;
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would send the email to a database or service

    alert('Thank you for your interest! We\'ll notify you when our online shop launches.');
    setEmail('');
  };
  
  return (
    <DuchessContainer>
      <ParallaxBanner 
        title="The Duchess Collection"
        subtitle="Elegant fashion and homeware inspired by Belvoir Castle"
        image={retailBanner}
      />
      
      <ContentContainer>
        <SectionTitle>The Duchess Collection</SectionTitle>
        <IntroText>
          The Duchess Collection offers a curated selection of elegant fashion and homeware inspired by the rich heritage and timeless beauty of Belvoir Castle. 
          Each piece reflects the refined taste and style of the Duchess of Rutland, bringing a touch of castle luxury into your home and wardrobe. 
          From exquisite silk scarves featuring patterns from the castle's historic wallpaper to handcrafted home accessories adorned with motifs from our 
          renowned art collection, every item tells a story of Belvoir's aristocratic heritage.
        </IntroText>
        
        <ComingSoonBanner>
          <ComingSoonContent>
            <ComingSoonTitle>Online Shop Now Available!</ComingSoonTitle>
            <ComingSoonText>
              We're excited to announce that The Duchess Collection is now available online! 
              Browse and purchase our exclusive fashion and homeware pieces from anywhere in the world, 
              bringing the elegance of Belvoir Castle into your everyday life.
            </ComingSoonText>
            <ShopButton to="/retail-village/shops/duchess-collection/shop">
              <FontAwesomeIcon icon={faShoppingBag} />
              Shop Now
            </ShopButton>
          </ComingSoonContent>
        </ComingSoonBanner>
        
        <CurrentTimeDisplay>
          Current Time: {formattedTime} on {currentDay}
        </CurrentTimeDisplay>
        
        <OpeningTimesCard>
          <CardImage image={retailImage}>
            <StatusBadge isOpen={isShopOpen()}>
              <FontAwesomeIcon icon={faClock} />
              {isShopOpen() ? 'Open Now' : 'Closed'}
            </StatusBadge>
          </CardImage>
          <CardContent>
            <CardTitle>The Duchess Collection Opening Hours</CardTitle>
            <TimeInfo>
              <DayLabel>{openingHours.days}</DayLabel>
              <Hours>{openingHours.times}</Hours>
              <Note>{openingHours.note}</Note>
            </TimeInfo>
            <p>
              Visit The Duchess Collection in person to explore our full range of products. Located in the Retail Village at Belvoir Castle, 
              our boutique offers a welcoming atmosphere where you can discover our exclusive designs.
            </p>
          </CardContent>
        </OpeningTimesCard>
        
        {/* <UpcomingDatesSection>
          <UpcomingDatesTitle>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Upcoming Important Dates
          </UpcomingDatesTitle>
          <DatesList>
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Normal Opening Hours
              </DateTitle>
              <DateRange>Daily</DateRange>
              <DateDetails>
                <strong>The Duchess Collection:</strong> 9:00am - 5:00pm daily
              </DateDetails>
            </DateCard>
            
            <DateCard $type="closed">
              <DateTitle>
                <FontAwesomeIcon icon={faTimes} />
                Christmas Day
              </DateTitle>
              <DateRange>Thursday 25th December 2025</DateRange>
              <DateDetails>
                <strong>Closed</strong>
              </DateDetails>
              <DateNote>We will be closed for Christmas Day</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Boxing Day
              </DateTitle>
              <DateRange>Friday 26th December 2025</DateRange>
              <DateDetails>
                <strong>The Duchess Collection:</strong> 9:00am - 3:30pm
              </DateDetails>
              <DateNote>Special Boxing Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                New Year's Day
              </DateTitle>
              <DateRange>Thursday 1st January 2026</DateRange>
              <DateDetails>
                <strong>The Duchess Collection:</strong> 9:00am - 3:30pm
              </DateDetails>
              <DateNote>Special New Year's Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faCheck} />
                Online Boutique
              </DateTitle>
              <DateRange>Now Available</DateRange>
              <DateDetails>
                <strong>Online Shop:</strong> Browse and purchase our exclusive collection online
              </DateDetails>
              <DateNote>Shop our elegant fashion and homeware pieces from anywhere in the world</DateNote>
            </DateCard>
          </DatesList>
        </UpcomingDatesSection> */}
        
        <SectionTitle>Our Collections</SectionTitle>
        <IntroText>
          The Duchess Collection features carefully crafted fashion and homeware pieces that embody the elegance and heritage of Belvoir Castle, 
          with designs inspired by the castle's rich history and stunning surroundings.
        </IntroText>
        
        <FeaturesGrid>
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faCrown} />
            </IconWrapper>
            <FeatureTitle>Elegant Apparel</FeatureTitle>
            <FeatureDescription>
              Timeless fashion pieces including scarves, wraps, and accessories crafted from the finest materials and featuring designs inspired by the castle's art collection.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faCouch} />
            </IconWrapper>
            <FeatureTitle>Luxury Homeware</FeatureTitle>
            <FeatureDescription>
              Exquisite home accessories including cushions, throws, and tableware that bring the elegance of Belvoir Castle into your living space.
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faPalette} />
            </IconWrapper>
            <FeatureTitle>Heritage-Inspired Designs</FeatureTitle>
            <FeatureDescription>
              Each piece tells a story, with patterns and motifs drawn from the castle's architecture, gardens, and centuries of history.
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
        
        <EmailSignupSection>
          <EmailSignupTitle>Be the First to Know</EmailSignupTitle>
          <EmailSignupText>
            Sign up to receive updates about our online shop launch and be the first to access our exclusive collection of fashion and homeware pieces online. 
            As a subscriber, you'll receive early access to new collections and special offers.
          </EmailSignupText>
          <EmailForm onSubmit={handleEmailSubmit}>
            <EmailInput 
              type="email" 
              placeholder="Your email address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <SubmitButton type="submit">Notify Me</SubmitButton>
          </EmailForm>
        </EmailSignupSection>
        
        <SectionTitle>Visit Our Boutique</SectionTitle>
        <IntroText>
          While you wait for our online shop, we invite you to visit The Duchess Collection boutique in person. 
          Located in the Retail Village at Belvoir Castle, our shop offers a curated selection of elegant fashion and homeware pieces 
          that embody the timeless beauty and heritage of the castle.
        </IntroText>
      </ContentContainer>
      
      <Footer />
    </DuchessContainer>
  );
};

export default DuchessCollection; 